import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  OnInit,
  OnChanges,
  SimpleChanges,
  forwardRef
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

@Component({
  selector: "app-search-select",
  templateUrl: "./search_select.html",
  styleUrls: ["./search_select.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchSelectComponent),
      multi: true
    }
  ]
})
export class SearchSelectComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() id: string = "search-select-" + Math.random().toString(36).substr(2, 9);
  @Input() label: string = "";
  @Input() placeholder: string = "Seleccionar...";
  @Input() items: any[] = [];
  @Input() displayKey: string = "name"; // Propiedad a mostrar del item
  @Input() valueKey: string = "id"; // Propiedad del valor del item
  @Input() disabled: boolean = false;
  /** Controls where the dropdown opens: 'above', 'below', or 'auto' (detects available space). */
  @Input() dropdownPosition: "above" | "below" | "auto" = "auto";

  @Output() itemSelected = new EventEmitter<any>();
  /** Texto escrito o al abrir la lista; permite buscar en el servidor en vez de cargar todo. */
  @Output() searchChange = new EventEmitter<string>();

  showDropdown: boolean = false;
  openUpward: boolean = false;
  filteredItems: any[] = [];
  selectedItem: any = null;
  displayValue: string = "";
  highlightedIndex: number = -1; // Índice del item resaltado con teclado

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  private pendingValue: any = null; // Valor pendiente cuando llega antes que los items

  constructor(private elementRef: ElementRef) {}

  ngOnInit() {
    this.filteredItems = [...this.items];

    // Si hay un valor pendiente y ya tenemos items al iniciar, procesarlo
    if (this.pendingValue !== null && this.items.length > 0) {
      this.setValueFromItems(this.pendingValue);
      this.pendingValue = null;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.items && changes.items.currentValue) {
      this.filteredItems = [...this.items];

      // Si hay un valor pendiente, intentar establecerlo ahora que tenemos items
      if (this.pendingValue !== null && this.items.length > 0) {
        this.setValueFromItems(this.pendingValue);
        this.pendingValue = null;
      }
      // Si hay un selectedItem y no está en la nueva lista, limpiar
      else if (this.selectedItem) {
        const itemExists = this.items.find((i) => {
          if (this.valueKey) {
            return i[this.valueKey] === this.selectedItem[this.valueKey];
          }
          return i === this.selectedItem;
        });
        if (!itemExists) {
          this.clearSelection();
        }
      }
    }
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent) {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside && this.showDropdown) {
      this.closeDropdown();
    }
  }

  @HostListener("keydown", ["$event"])
  onKeyDown(event: KeyboardEvent) {
    if (this.disabled) return;

    if (!this.showDropdown && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      // Abrir dropdown con flechas
      event.preventDefault();
      this.calculateDropdownDirection();
      this.showDropdown = true;
      this.filterItems();
      this.highlightedIndex = 0;
      return;
    }

    if (!this.showDropdown) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (this.filteredItems.length > 0) {
          this.highlightedIndex = Math.min(
            this.highlightedIndex + 1,
            this.filteredItems.length - 1
          );
          this.scrollToHighlighted();
        }
        break;

      case "ArrowUp":
        event.preventDefault();
        if (this.filteredItems.length > 0) {
          this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
          this.scrollToHighlighted();
        }
        break;

      case "Enter":
        event.preventDefault();
        if (this.highlightedIndex >= 0 && this.highlightedIndex < this.filteredItems.length) {
          this.selectItem(this.filteredItems[this.highlightedIndex]);
        }
        break;

      case "Escape":
        event.preventDefault();
        this.closeDropdown();
        break;
    }
  }

  onInputFocus() {
    if (!this.disabled) {
      this.calculateDropdownDirection();
      this.showDropdown = true;
      this.filterItems();
      this.highlightedIndex = -1;
      this.searchChange.emit(this.displayValue || "");
    }
  }

  toggleDropdown() {
    if (!this.disabled) {
      this.showDropdown = !this.showDropdown;
      if (this.showDropdown) {
        this.calculateDropdownDirection();
        this.filterItems();
      }
    }
  }

  onDisplayValueChange() {
    this.calculateDropdownDirection();
    this.showDropdown = true;
    this.filterItems();
    this.searchChange.emit(this.displayValue || "");
    // Si el valor no coincide con el selectedItem, limpiar la selección
    if (this.selectedItem && this.displayValue !== this.getDisplayValue(this.selectedItem)) {
      this.selectedItem = null;
      this.onChange(null);
      this.itemSelected.emit(null);
    }
  }

  filterItems() {
    if (this.displayValue && this.displayValue.trim().length > 0) {
      this.filteredItems = this.items.filter((item: any) => {
        const itemDisplayValue = this.getDisplayValue(item);
        return (
          itemDisplayValue &&
          itemDisplayValue.toLowerCase().includes(this.displayValue.toLowerCase())
        );
      });
    } else {
      this.filteredItems = [...this.items];
    }
    this.highlightedIndex = -1; // Resetear al filtrar
  }

  closeDropdown() {
    this.showDropdown = false;
    this.highlightedIndex = -1;
    this.onTouched();
  }

  selectItem(item: any) {
    this.selectedItem = item;
    this.displayValue = this.getDisplayValue(item);
    this.showDropdown = false;

    const value = this.valueKey ? item[this.valueKey] : item;
    this.onChange(value);
    this.itemSelected.emit(item);
  }

  clearSelection() {
    this.selectedItem = null;
    this.displayValue = "";
    this.filteredItems = [...this.items];
    this.onChange(null);
    this.itemSelected.emit(null);
  }

  getDisplayValue(item: any): string {
    if (!item) return "";

    if (this.displayKey === "subclient_fullname") {
      return item.user.first_name + " " + item.user.last_name;
    }
    return this.displayKey ? item[this.displayKey] : item.toString();
  }

  getSelectedItem(): any {
    return this.selectedItem;
  }

  getSelectedDisplayValue(): string {
    return this.displayValue;
  }

  isSelected(item: any): boolean {
    if (!this.selectedItem) return false;
    if (this.valueKey) {
      return this.selectedItem[this.valueKey] === item[this.valueKey];
    }
    return this.selectedItem === item;
  }

  isHighlighted(index: number): boolean {
    return this.highlightedIndex === index;
  }

  /**
   * Calculates whether the dropdown should open upward or downward based on available viewport space.
   */
  private calculateDropdownDirection(): void {
    if (this.dropdownPosition === "above") {
      this.openUpward = true;
      return;
    }
    if (this.dropdownPosition === "below") {
      this.openUpward = false;
      return;
    }
    const wrapperEl = this.elementRef.nativeElement as HTMLElement;
    const rect = wrapperEl.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const dropdownMaxHeight = 300;
    this.openUpward = spaceBelow < dropdownMaxHeight && rect.top > dropdownMaxHeight;
  }

  private scrollToHighlighted(): void {
    setTimeout(() => {
      const highlightedElement = this.elementRef.nativeElement.querySelector(
        ".dropdown-item.highlighted"
      );
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: "nearest",
          behavior: "smooth"
        });
      }
    }, 0);
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    if (value !== undefined && value !== null) {
      if (this.items && this.items.length > 0) {
        // Si ya tenemos items, buscar y establecer el valor
        this.setValueFromItems(value);
      } else {
        // Si no tenemos items aún, guardar el valor para procesarlo después
        this.pendingValue = value;
      }
    } else {
      // Limpiar sin llamar a onChange para evitar bucle infinito
      this.selectedItem = null;
      this.displayValue = "";
      this.filteredItems = [...this.items];
      this.pendingValue = null;
    }
  }

  private setValueFromItems(value: any): void {
    const item = this.items.find((i) => {
      if (this.valueKey) {
        return i[this.valueKey] === value;
      }
      return i === value;
    });

    if (item) {
      this.selectedItem = item;
      this.displayValue = this.getDisplayValue(item);
    } else {
      // Si no se encuentra el item, limpiar sin llamar a onChange
      this.selectedItem = null;
      this.displayValue = "";
      this.filteredItems = [...this.items];
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
